/**
 * Core data model.
 *
 * The in-memory song format is a superset of XM: it can represent everything an
 * XM can, and MOD files are widened into it on load. That keeps a single
 * replayer able to serve both formats, with per-song flags selecting the
 * ProTracker or FastTracker II behaviours where the two genuinely disagree.
 */

/** Which tracker's quirks the replayer should reproduce for this song. */
export type PlayerMode = 'pt' | 'ft2';

/** Loop styles a sample can use. XM adds ping-pong; MOD only ever has forward. */
export const LOOP_NONE = 0;
export const LOOP_FORWARD = 1;
export const LOOP_PINGPONG = 2;
export type LoopType = typeof LOOP_NONE | typeof LOOP_FORWARD | typeof LOOP_PINGPONG;

export interface Sample {
  name: string;
  /** Sample data normalised to -1..1. Length is the authoritative frame count. */
  data: Float32Array;
  length: number;
  loopStart: number;
  loopLength: number;
  loopType: LoopType;
  /** 0..64, ProTracker/FT2 units. */
  volume: number;
  /** -128..127, where ±128 is one semitone. MOD's -8..7 is stored ×16. */
  finetune: number;
  /** 0..255 panning, 128 = centre. MOD ignores this in favour of Amiga panning. */
  panning: number;
  /** XM per-sample transpose in semitones, -96..95. */
  relativeNote: number;
  /** Bit depth the sample was stored at; kept so writers can round-trip it. */
  bits: 8 | 16;
}

export interface EnvelopePoint {
  /** Tick offset from note start, 0..65535. */
  x: number;
  /** 0..64 for volume; 0..64 for panning where 32 is centre. */
  y: number;
}

export interface Envelope {
  points: EnvelopePoint[];
  enabled: boolean;
  sustainEnabled: boolean;
  loopEnabled: boolean;
  sustainPoint: number;
  loopStart: number;
  loopEnd: number;
}

export function emptyEnvelope(defaultY: number): Envelope {
  return {
    points: [
      { x: 0, y: defaultY },
      { x: 24, y: defaultY },
    ],
    enabled: false,
    sustainEnabled: false,
    loopEnabled: false,
    sustainPoint: 0,
    loopStart: 0,
    loopEnd: 1,
  };
}

/** Autovibrato waveform selector (XM instrument level). */
export const VIB_SINE = 0;
export const VIB_SQUARE = 1;
export const VIB_RAMP_DOWN = 2;
export const VIB_RAMP_UP = 3;

export interface Instrument {
  name: string;
  samples: Sample[];
  /** 96 entries (notes C-0..B-7) mapping a note to an index into `samples`. */
  sampleMap: Uint8Array;
  volumeEnvelope: Envelope;
  panningEnvelope: Envelope;
  vibratoType: number;
  vibratoSweep: number;
  vibratoDepth: number;
  vibratoRate: number;
  /** 0..32767; subtracted from a 65536-scale fade counter each tick after key-off. */
  volumeFadeout: number;
}

/**
 * A pattern cell. Stored as five parallel bytes per cell inside `Pattern.data`
 * rather than as objects — a 64-row × 32-channel pattern is 2048 cells, and the
 * flat layout keeps both the editor and the worklet allocation-free.
 */
export const CELL_STRIDE = 5;
export const CELL_NOTE = 0;
export const CELL_INSTRUMENT = 1;
export const CELL_VOLUME = 2;
export const CELL_EFFECT = 3;
export const CELL_PARAM = 4;

/** Note values: 0 = empty, 1..96 = C-0..B-7, 97 = key off. */
export const NOTE_NONE = 0;
export const NOTE_KEYOFF = 97;

export interface Pattern {
  rows: number;
  /** Length is rows * channels * CELL_STRIDE. */
  data: Uint8Array;
}

export interface Song {
  name: string;
  /** Free-text tracker identifier, e.g. "FastTracker v2.00" or "M.K.". */
  trackerName: string;
  format: 'mod' | 'xm';
  mode: PlayerMode;
  channels: number;
  /** Order table; entries index into `patterns`. */
  order: Uint8Array;
  songLength: number;
  restartPosition: number;
  patterns: Pattern[];
  instruments: Instrument[];
  initialSpeed: number;
  initialTempo: number;
  /** XM frequency table selector. MOD is always Amiga. */
  linearFrequency: boolean;
  /** Clamp periods to the Amiga's hardware range (113..856). ProTracker only. */
  amigaLimits: boolean;
  /** Default panning per channel, 0..255. */
  defaultPanning: Uint8Array;
}

export function createPattern(rows: number, channels: number): Pattern {
  return { rows, data: new Uint8Array(rows * channels * CELL_STRIDE) };
}

export function cellOffset(channels: number, row: number, channel: number): number {
  return (row * channels + channel) * CELL_STRIDE;
}

export function createSample(name = ''): Sample {
  return {
    name,
    data: new Float32Array(0),
    length: 0,
    loopStart: 0,
    loopLength: 0,
    loopType: LOOP_NONE,
    volume: 64,
    finetune: 0,
    panning: 128,
    relativeNote: 0,
    bits: 8,
  };
}

export function createInstrument(name = ''): Instrument {
  return {
    name,
    samples: [],
    sampleMap: new Uint8Array(96),
    volumeEnvelope: emptyEnvelope(64),
    panningEnvelope: emptyEnvelope(32),
    vibratoType: VIB_SINE,
    vibratoSweep: 0,
    vibratoDepth: 0,
    vibratoRate: 0,
    volumeFadeout: 0,
  };
}

/** Diagnostics a loader can attach so the UI can surface odd files. */
export interface LoadWarning {
  code: string;
  message: string;
}

export interface LoadResult {
  song: Song;
  warnings: LoadWarning[];
}
